import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  ApplStatusEntity,
  ClassComponentTypeEntity,
  LanguageEntity,
  MajorEntity,
  StudyLevelEntity,
  StudyYearEntity,
  TeachingModeEntity,
  TimeZoneEntity,
  UniversityEntity,
  SiteMessageEntity,
} from 'vse-material-ui';
import { VseSharedDataState } from './sharedDataTypes';
import { useTypedSelector } from '../../store';
import * as VseSession from '../session';
import * as VseAlert from '../alert';

const initialState = {
  applStatusLoading: 'idle',
  applStatusOptions: [],
  classComponentTypeLoading: 'idle',
  classComponentTypeOptions: [],
  languageLoading: 'idle',
  languageOptions: [],
  majorLoading: 'idle',
  majorOptions: [],
  seasonLoading: 'idle',
  seasonOptions: [],
  studyLevelLoading: 'idle',
  studyLevelOptions: [],
  studyYearLoading: 'idle',
  studyYearOptions: [],
  teachingModeLoading: 'idle',
  teachingModeOptions: [],
  timeZoneLoading: 'idle',
  timeZoneOptions: [],
  universityLoading: 'idle',
  universityOptions: [],
  siteMessageLoading: 'idle',
  siteMessageOptions: [],
} as VseSharedDataState;

export const loadApplStatuses = createAsyncThunk('sharedData/loadApplStatuses', async (_, thunkApi) => {
  try {
    const response = await axios.get<ApplStatusEntity[]>('api/shared/applstatuses');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadClassComponentTypes = createAsyncThunk('sharedData/loadClassComponentTypes', async (_, thunkApi) => {
  try {
    const response = await axios.get<ClassComponentTypeEntity[]>('api/shared/classcomponentTypes');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadLanguages = createAsyncThunk('sharedData/loadLanguages', async (_, thunkApi) => {
  try {
    const response = await axios.get<LanguageEntity[]>('api/shared/languages');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadMajors = createAsyncThunk('sharedData/loadMajors', async (_, thunkApi) => {
  try {
    const response = await axios.get<MajorEntity[]>('api/shared/majors');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadStudyLevels = createAsyncThunk('sharedData/loadStudyLevels', async (_, thunkApi) => {
  try {
    const response = await axios.get<StudyLevelEntity[]>('api/shared/studylevels');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadStudyYears = createAsyncThunk('sharedData/loadStudyYears', async (_, thunkApi) => {
  try {
    const response = await axios.get<StudyYearEntity[]>('api/shared/studyyears');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadTeachingModes = createAsyncThunk('sharedData/loadTeachingModes', async (_, thunkApi) => {
  try {
    const response = await axios.get<TeachingModeEntity[]>('api/shared/teachingmodes');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadTimeZones = createAsyncThunk('sharedData/loadTimeZones', async (_, thunkApi) => {
  try {
    const response = await axios.get<TimeZoneEntity[]>('api/shared/timezones');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadUniversities = createAsyncThunk('sharedData/loadUniversities', async (_, thunkApi) => {
  try {
    const response = await axios.get<UniversityEntity[]>('api/shared/universities');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const loadSiteMessages = createAsyncThunk('sharedData/loadSiteMessages', async (_, thunkApi) => {
  try {
    const response = await axios.get<SiteMessageEntity[]>('api/shared/sitemessages');
    return response;
  } catch (err) {
    const error: AxiosError = err;
    if (!VseAlert.tryHandleTimeout(thunkApi, error)) {
      VseAlert.handleThunkError(thunkApi, error);
    }
    return thunkApi.rejectWithValue(error);
  }
});

export const sharedDataSlice = createSlice({
  name: 'sharedData',
  initialState,
  reducers: {
  },
  extraReducers: builder => {
    builder.addCase(VseSession.reset, (state) => {
      state = Object.assign(state, initialState);
    });
    builder.addCase(loadApplStatuses.pending, (state) => {
      state.applStatusLoading = 'pending';
    });
    builder.addCase(loadApplStatuses.fulfilled, (state, action) => {
      state.applStatusLoading = 'succeeded';
      state.applStatusOptions = action.payload.data;
    });
    builder.addCase(loadApplStatuses.rejected, (state) => {
      state.applStatusLoading = 'failed';
      state.applStatusOptions = initialState.applStatusOptions;
    });
    builder.addCase(loadClassComponentTypes.pending, (state) => {
      state.classComponentTypeLoading = 'pending';
    });
    builder.addCase(loadClassComponentTypes.fulfilled, (state, action) => {
      state.classComponentTypeLoading = 'succeeded';
      state.classComponentTypeOptions = action.payload.data;
    });
    builder.addCase(loadClassComponentTypes.rejected, (state) => {
      state.classComponentTypeLoading = 'failed';
      state.classComponentTypeOptions = initialState.classComponentTypeOptions;
    });
    builder.addCase(loadLanguages.pending, (state) => {
      state.languageLoading = 'pending';
    });
    builder.addCase(loadLanguages.fulfilled, (state, action) => {
      state.languageLoading = 'succeeded';
      state.languageOptions = action.payload.data;
    });
    builder.addCase(loadLanguages.rejected, (state) => {
      state.languageLoading = 'failed';
      state.languageOptions = initialState.languageOptions;
    });
    builder.addCase(loadMajors.pending, (state) => {
      state.majorLoading = 'pending';
    });
    builder.addCase(loadMajors.fulfilled, (state, action) => {
      state.majorLoading = 'succeeded';
      state.majorOptions = action.payload.data;
    });
    builder.addCase(loadMajors.rejected, (state) => {
      state.majorLoading = 'failed';
      state.majorOptions = initialState.majorOptions;
    });
    builder.addCase(loadTeachingModes.pending, (state) => {
      state.teachingModeLoading = 'pending';
    });
    builder.addCase(loadTeachingModes.fulfilled, (state, action) => {
      state.teachingModeLoading = 'succeeded';
      state.teachingModeOptions = action.payload.data;
    });
    builder.addCase(loadTeachingModes.rejected, (state) => {
      state.teachingModeLoading = 'failed';
      state.teachingModeOptions = initialState.teachingModeOptions;
    });
    builder.addCase(loadTimeZones.pending, (state) => {
      state.timeZoneLoading = 'pending';
    });
    builder.addCase(loadTimeZones.fulfilled, (state, action) => {
      state.timeZoneLoading = 'succeeded';
      state.timeZoneOptions = action.payload.data;
    });
    builder.addCase(loadTimeZones.rejected, (state) => {
      state.timeZoneLoading = 'failed';
      state.timeZoneOptions = initialState.timeZoneOptions;
    });
    builder.addCase(loadStudyLevels.pending, (state) => {
      state.studyLevelLoading = 'pending';
    });
    builder.addCase(loadStudyLevels.fulfilled, (state, action) => {
      state.studyLevelLoading = 'succeeded';
      state.studyLevelOptions = action.payload.data;
    });
    builder.addCase(loadStudyLevels.rejected, (state) => {
      state.studyLevelLoading = 'failed';
      state.studyLevelOptions = initialState.studyLevelOptions;
    });
    builder.addCase(loadStudyYears.pending, (state) => {
      state.studyYearLoading = 'pending';
    });
    builder.addCase(loadStudyYears.fulfilled, (state, action) => {
      state.studyYearLoading = 'succeeded';
      state.studyYearOptions = action.payload.data;
    });
    builder.addCase(loadStudyYears.rejected, (state) => {
      state.studyYearLoading = 'failed';
      state.studyYearOptions = initialState.studyYearOptions;
    });
    builder.addCase(loadUniversities.pending, (state) => {
      state.universityLoading = 'pending';
    });
    builder.addCase(loadUniversities.fulfilled, (state, action) => {
      state.universityLoading = 'succeeded';
      state.universityOptions = action.payload.data;
    });
    builder.addCase(loadUniversities.rejected, (state) => {
      state.universityLoading = 'failed';
      state.universityOptions = initialState.universityOptions;
    });
    builder.addCase(loadSiteMessages.pending, (state) => {
      state.siteMessageLoading = 'pending';
    });
    builder.addCase(loadSiteMessages.fulfilled, (state, action) => {
      state.siteMessageLoading = 'succeeded';
      state.siteMessageOptions = action.payload.data;
    });
    builder.addCase(loadSiteMessages.rejected, (state) => {
      state.siteMessageLoading = 'failed';
      state.siteMessageOptions = initialState.siteMessageOptions;
    });
  }
});

export default sharedDataSlice.reducer;

export const useVseSharedDataState = (): VseSharedDataState => useTypedSelector(state => state.sharedData);
